import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  X,
  ChevronRight,
  Shield,
  ShieldOff,
  UserX,
  UserCheck,
  Bot,
  BotOff,
  CreditCard,
} from 'lucide-react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { api } from '../../api/client';

type Plan = 'free' | 'starter' | 'pro' | 'agency';

interface AdminUser {
  uid: string;
  email: string;
  plan: Plan;
  farmEnabled: boolean;
  created_at: string;
  last_seen: string;
  banned: boolean;
  channels_count: number;
}

interface UserChannel {
  id: string;
  title: string;
  username: string;
  subscribers: number;
}

interface UserPayment {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: string;
}

interface UserAction {
  id: string;
  action: string;
  occurred_at: string;
}

interface UserDetail {
  user: AdminUser;
  channels: UserChannel[];
  recent_actions: UserAction[];
  payments: UserPayment[];
}

const PLANS: Plan[] = ['free', 'starter', 'pro', 'agency'];

const PLAN_BADGE: Record<Plan, string> = {
  free: 'bg-slate-100 text-slate-600',
  starter: 'bg-blue-100 text-blue-700',
  pro: 'bg-purple-100 text-purple-700',
  agency: 'bg-amber-100 text-amber-700',
};

async function fetchUsers(search: string, plan: string): Promise<AdminUser[]> {
  const { data } = await api.get<AdminUser[]>('/admin/users', {
    params: { search: search || undefined, plan: plan !== 'all' ? plan : undefined },
  });
  return data;
}

async function fetchUserDetail(uid: string): Promise<UserDetail> {
  const { data } = await api.get<UserDetail>(`/admin/users/${uid}`);
  return data;
}

function PlanBadge({ plan }: { plan: Plan }): JSX.Element {
  return (
    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-semibold', PLAN_BADGE[plan])}>
      {plan}
    </span>
  );
}

function UserDetailPanel({
  uid,
  onClose,
}: {
  uid: string;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ['admin', 'user-detail', uid],
    queryFn: () => fetchUserDetail(uid),
  });

  const setPlanMut = useMutation<void, Error, Plan>({
    mutationFn: async (plan) => {
      await api.patch(`/admin/users/${uid}`, { plan });
    },
    onSuccess: () => {
      toast.success('Plan updated');
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'user-detail', uid] });
    },
    onError: (e) => toast.error(e.message),
  });

  const toggleFarmMut = useMutation<void, Error, boolean>({
    mutationFn: async (enabled) => {
      await api.patch(`/admin/users/${uid}`, { farmEnabled: enabled });
    },
    onSuccess: () => {
      toast.success('Farm access updated');
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'user-detail', uid] });
    },
    onError: (e) => toast.error(e.message),
  });

  const toggleBanMut = useMutation<void, Error, boolean>({
    mutationFn: async (banned) => {
      await api.patch(`/admin/users/${uid}`, { banned });
    },
    onSuccess: (_, banned) => {
      toast.success(banned ? 'User banned' : 'User unbanned');
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'user-detail', uid] });
    },
    onError: (e) => toast.error(e.message),
  });

  const d = detailQ.data;

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-30 flex flex-col border-l border-slate-200">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
        <h2 className="text-lg font-semibold text-brand-ink">User Detail</h2>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {detailQ.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : !d ? (
          <p className="text-brand-muted">Failed to load user details.</p>
        ) : (
          <>
            {/* User info */}
            <div className="card space-y-2">
              <div className="font-semibold text-brand-ink">{d.user.email}</div>
              <div className="flex items-center gap-3 flex-wrap">
                <PlanBadge plan={d.user.plan} />
                {d.user.farmEnabled && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                    Farm ON
                  </span>
                )}
                {d.user.banned && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    Banned
                  </span>
                )}
              </div>
              <div className="text-xs text-brand-muted space-y-0.5">
                <div>UID: {d.user.uid}</div>
                <div>Joined: {dayjs(d.user.created_at).format('DD MMM YYYY, HH:mm')}</div>
                <div>Last seen: {dayjs(d.user.last_seen).format('DD MMM YYYY, HH:mm')}</div>
                <div>Channels: {d.user.channels_count}</div>
              </div>
            </div>

            {/* Actions */}
            <div className="card space-y-2">
              <div className="text-sm font-semibold text-brand-ink mb-3">Actions</div>

              <div>
                <div className="text-xs text-brand-muted mb-1.5">Set Plan</div>
                <div className="flex gap-1.5 flex-wrap">
                  {PLANS.map((p) => (
                    <button
                      key={p}
                      onClick={() => setPlanMut.mutate(p)}
                      disabled={setPlanMut.isPending || d.user.plan === p}
                      className={clsx(
                        'px-3 py-1 rounded-full text-xs font-medium border transition',
                        d.user.plan === p
                          ? 'bg-brand-primary text-white border-brand-primary'
                          : 'border-slate-200 text-brand-ink hover:border-brand-primary',
                      )}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-1 flex-wrap">
                <button
                  onClick={() => toggleFarmMut.mutate(!d.user.farmEnabled)}
                  disabled={toggleFarmMut.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 transition"
                >
                  {d.user.farmEnabled ? (
                    <><BotOff size={14} className="text-orange-500" /> Disable Farm</>
                  ) : (
                    <><Bot size={14} className="text-emerald-500" /> Enable Farm</>
                  )}
                </button>

                <button
                  onClick={() => toggleBanMut.mutate(!d.user.banned)}
                  disabled={toggleBanMut.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm hover:bg-slate-50 transition"
                >
                  {d.user.banned ? (
                    <><UserCheck size={14} className="text-emerald-500" /> Unban</>
                  ) : (
                    <><UserX size={14} className="text-red-500" /> Ban User</>
                  )}
                </button>
              </div>
            </div>

            {/* Channels */}
            {d.channels.length > 0 && (
              <div className="card">
                <div className="text-sm font-semibold text-brand-ink mb-3">
                  Channels ({d.channels.length})
                </div>
                <ul className="divide-y divide-slate-100">
                  {d.channels.map((ch) => (
                    <li key={ch.id} className="py-2 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-brand-ink">{ch.title}</div>
                        <div className="text-xs text-brand-muted">@{ch.username}</div>
                      </div>
                      <div className="text-xs text-brand-muted">{ch.subscribers.toLocaleString()} subs</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recent Actions */}
            {d.recent_actions.length > 0 && (
              <div className="card">
                <div className="text-sm font-semibold text-brand-ink mb-3">Recent Actions</div>
                <ul className="divide-y divide-slate-100">
                  {d.recent_actions.slice(0, 10).map((a) => (
                    <li key={a.id} className="py-2 flex items-center justify-between">
                      <span className="text-sm text-brand-ink">{a.action}</span>
                      <span className="text-xs text-brand-muted">
                        {dayjs(a.occurred_at).format('DD MMM, HH:mm')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Payments */}
            {d.payments.length > 0 && (
              <div className="card">
                <div className="text-sm font-semibold text-brand-ink mb-3 flex items-center gap-1.5">
                  <CreditCard size={14} /> Payments
                </div>
                <ul className="divide-y divide-slate-100">
                  {d.payments.map((p) => (
                    <li key={p.id} className="py-2 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">
                          {p.amount} {p.currency}
                        </div>
                        <div className="text-xs text-brand-muted">{dayjs(p.date).format('DD MMM YYYY')}</div>
                      </div>
                      <span
                        className={clsx(
                          'px-2 py-0.5 rounded-full text-xs font-medium',
                          p.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600',
                        )}
                      >
                        {p.status}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function AdminUsers(): JSX.Element {
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<'all' | Plan>('all');
  const [selectedUid, setSelectedUid] = useState<string | null>(null);

  const usersQ = useQuery({
    queryKey: ['admin', 'users', search, planFilter],
    queryFn: () => fetchUsers(search, planFilter),
    placeholderData: (prev) => prev,
  });

  const users = usersQ.data ?? [];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-brand-ink mb-5">Users</h1>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text"
            placeholder="Search by email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-8 w-full"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-ink"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="flex gap-1.5">
          {(['all', ...PLANS] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPlanFilter(p)}
              className={clsx(
                'px-3 py-1.5 rounded-lg text-sm font-medium transition border',
                planFilter === p
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'border-slate-200 text-brand-ink hover:border-slate-300',
              )}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        {usersQ.isLoading ? (
          <div className="space-y-2 p-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        ) : users.length === 0 ? (
          <p className="text-brand-muted text-sm p-4">No users found.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-brand-muted border-b border-slate-100">
                <th className="pb-3 font-medium">Email</th>
                <th className="pb-3 font-medium">Plan</th>
                <th className="pb-3 font-medium">Farm</th>
                <th className="pb-3 font-medium">Created</th>
                <th className="pb-3 font-medium">Last Seen</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.uid}
                  className={clsx(
                    'border-b border-slate-50 last:border-0 cursor-pointer transition-colors',
                    selectedUid === u.uid ? 'bg-brand-primary/5' : 'hover:bg-slate-50',
                  )}
                  onClick={() => setSelectedUid(u.uid === selectedUid ? null : u.uid)}
                >
                  <td className="py-3 font-medium text-brand-ink">{u.email}</td>
                  <td className="py-3">
                    <PlanBadge plan={u.plan} />
                  </td>
                  <td className="py-3">
                    {u.farmEnabled ? (
                      <span className="text-emerald-600 flex items-center gap-1">
                        <Bot size={14} /> On
                      </span>
                    ) : (
                      <span className="text-brand-muted">Off</span>
                    )}
                  </td>
                  <td className="py-3 text-brand-muted">
                    {dayjs(u.created_at).format('DD MMM YY')}
                  </td>
                  <td className="py-3 text-brand-muted">
                    {dayjs(u.last_seen).format('DD MMM YY')}
                  </td>
                  <td className="py-3">
                    {u.banned ? (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-600 font-medium">
                        Banned
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700 font-medium">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-brand-muted">
                    <ChevronRight
                      size={16}
                      className={clsx(
                        'transition-transform',
                        selectedUid === u.uid && 'rotate-90',
                      )}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Slide-out detail panel */}
      {selectedUid && (
        <>
          <div
            className="fixed inset-0 bg-black/20 z-20"
            onClick={() => setSelectedUid(null)}
          />
          <UserDetailPanel uid={selectedUid} onClose={() => setSelectedUid(null)} />
        </>
      )}
    </div>
  );
}
